/**
 * Оболочка приложения.
 *
 * Отвечает ровно на два вопроса: вошёл ли человек и какой раздел
 * открыт. Ни о рынке, ни о графике, ни о разделах по существу она не
 * знает — разделы берутся из `nav/sections`, и добавленный туда
 * появляется здесь сам.
 *
 * Терминал остался нетронутым. Он по-прежнему держит свою разметку во
 * всю высоту и свои панели; оболочка лишь ставит над ним меню и не
 * лезет внутрь.
 */

import { Component, Suspense, useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { Signal } from '@specter/types';
import { AppNav } from './components/AppNav.js';
import { MissionCoach } from './journey/MissionCoach.js';
import { Welcome } from './journey/Welcome.js';
import './journey/journey.css';
import { AuthScreen } from './components/AuthScreen.js';
import { coreApi } from './api/client.js';
import { DEFAULT_SECTION, findSection } from './nav/sections.js';
import { ensureRoute, useRoute } from './nav/use-route.js';
import { useTerminal } from './state/store.js';
import { useThemeSetting } from './state/theme.js';
import { useLanguageSetting } from './i18n/use-language.js';
import { insideTelegram, prepareTelegramWindow, signInFromTelegram } from './state/telegram-app.js';
import { unreadCount, useSeenAt } from './state/seen.js';
import { refreshUser, useSession } from './state/session.js';
import { useSettingsSync } from './state/use-settings-sync.js';
import { useCoreStream } from './state/use-core-stream.js';

/**
 * Как часто пересчитывать непрочитанное.
 *
 * Значок над колокольчиком — не поток данных: сигналы приходят раз в
 * несколько часов, и опрашивать чаще минуты значит греть сеть ради
 * числа, которое почти никогда не меняется. Поток терминала для этого
 * не годится: он живёт, только пока открыт график.
 */
const UNREAD_POLL_MS = 60_000;

export function Shell(): JSX.Element {
  const session = useSession();
  /*
   * Пока Telegram подтверждает, кто пришёл, экран входа не показывается.
   *
   * Иначе человек, открывший мини-приложение, на долю секунды видит
   * «Войдите через Telegram» — находясь в Telegram. Выглядит это как
   * поломка, а не как загрузка.
   */
  const [asking, setAsking] = useState(() => insideTelegram() && !session);

  // Окно Telegram готовится при каждом открытии, а не только при входе.
  useEffect(() => {
    if (insideTelegram()) prepareTelegramWindow();
  }, []);

  useEffect(() => {
    if (!asking) return;

    let live = true;
    void signInFromTelegram().finally(() => {
      if (live) setAsking(false);
    });

    return () => {
      live = false;
    };
  }, [asking]);

  if (asking) return <div className="shell-wait" />;
  if (!session) return <AuthScreen />;
  return <Signed />;
}

function Signed(): JSX.Element {
  /*
   * Профиль грузится здесь, а не в графике.
   *
   * Он принадлежит сеансу: настройки уведомлений стоят в кабинете,
   * пороги применяются в терминале, а рабочее место восстанавливается
   * на графике. Привязать загрузку к одному из разделов значило бы,
   * что в остальных человек видит умолчания вместо своего профиля — и
   * правит их впустую.
   */
  useSettingsSync();

  /*
   * Поток тоже принадлежит сеансу.
   *
   * Пока он жил в графике, каждый переход в другой раздел рвал сокет и
   * снимал наблюдение в Core — с повторным прогревом и двадцатью
   * секундами без формирующейся свечи на возврате.
   */
  useCoreStream();

  /*
   * Сведения о владельце — с сервера, один раз за загрузку.
   *
   * В браузере они лежат ради того, чтобы имя было видно сразу, ещё до
   * ответа Core. Но запись там могла появиться раньше, чем платформа
   * научилась новому: аватарки в старых записях нет вовсе, и без этого
   * запроса она не появилась бы до следующего входа — то есть, может, и
   * через месяц.
   */
  useEffect(() => {
    let live = true;
    void coreApi
      .me()
      .then(({ user }) => {
        if (live) refreshUser(user);
      })
      // Не вошли или Core молчит — показываем то, что помним.
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, []);

  /*
   * Тема и язык применяются здесь, а не в графике.
   *
   * Раньше это стояло в `App` — то есть в компоненте страницы графика.
   * Пока человек его не открыл, `data-theme` на документе не появлялся
   * вовсе, и главная, статистика и кабинет оставались тёмными при
   * выбранной светлой теме. Выглядело как «светлая тема не работает», а
   * не работала не она: её просто некому было включить.
   *
   * Оболочка обёрнута вокруг всех разделов и живёт весь сеанс — она и
   * есть верное место для того, что относится ко всему экрану.
   */
  /*
   * Тема одна — тёмная. Выбор «светлая / как в системе» убран 10.10.2026:
   * платформа рисуется под ночной фон, и светлый вид графика рядом с
   * ночными «Главной» и радаром выглядел недоделкой. Настройка в профиле
   * человека сохраняется, но не читается; если светлую тему решим
   * развивать, здесь снова встанет `settings.theme` и `watchSystemTheme`.
   */
  useEffect(() => {
    useThemeSetting('dark');
  }, []);

  useLanguageSetting(useTerminal((s) => s.settings.language));

  const [route, go] = useRoute();
  const unread = useUnread();
  const section = findSection(route) ?? findSection(DEFAULT_SECTION)!;
  const Page = section.page;

  useEffect(ensureRoute, []);

  return (
    <div className={section.bare ? 'shell shell--bare' : 'shell'}>
      <AppNav current={section.id} onGo={go} unread={unread} />

      <SyncNotice />

      {/*
        Ожидание — пустотой, а не крутилкой.

        Кусок раздела приезжает за десятки миллисекунд, и мигнувшая на
        это время надпись заметнее самой загрузки.
      */}
      <main className="shell-body">
        {/* Ключ — раздел: ушёл в другой раздел, и прошлая неудача забыта. */}
        <PageBoundary key={section.id} section={section.id}>
          <Suspense fallback={<PageWait />}>
            <Page />
          </Suspense>
        </PageBoundary>
      </main>

      {/* Путь человека: приветствие при первом входе и наставник миссий поверх любого раздела. */}
      <Welcome />
      <MissionCoach />
    </div>
  );
}

/**
 * Раздел, который не открылся, — сообщением с кнопкой, а не пустотой.
 *
 * Без этой границы ошибка раздела снимала всё приложение, и человек
 * видел серый экран, не понимая, ждать ему или что-то делать. Чаще всего
 * это кусок прошлой сборки после выкладки; `lazyPage` в этом случае уже
 * перезагружает страницу сам, а сюда попадает то, что перезагрузка не
 * вылечила.
 *
 * Но сюда же попадает и любая ошибка внутри раздела, к выкладке
 * отношения не имеющая. Раньше обе показывались одним текстом про
 * обновление, и настоящая причина оставалась в браузере человека.
 * Теперь граница различает их и присылает ошибку на сервер
 * (`/api/client-error`), а человеку показывает её коротко.
 */
class PageBoundary extends Component<{ children: ReactNode; section: string }, { failed: Error | null }> {
  override state: { failed: Error | null } = { failed: null };

  static getDerivedStateFromError(error: unknown): { failed: Error } {
    return { failed: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: { componentStack?: string | null }): void {
    const failure = error instanceof Error ? error : new Error(String(error));
    const bundle = [...document.scripts].map((script) => script.src.match(/index-[\w-]+\.js/)?.[0]).find(Boolean) ?? '';
    void coreApi
      .clientError({
        section: this.props.section,
        kind: isChunkError(failure) ? 'chunk' : 'render',
        message: failure.message,
        stack: `${failure.stack ?? ''}\n--- компоненты:${info.componentStack ?? ''}`,
        bundle,
        agent: navigator.userAgent,
      })
      .catch(() => undefined);
  }

  override render(): ReactNode {
    const failed = this.state.failed;
    if (!failed) return this.props.children;
    if (isChunkError(failed)) {
      return <PageTrouble text="Раздел не открылся: вышло обновление, а страница осталась от прежней версии." />;
    }
    return <PageTrouble text="В разделе случилась ошибка. Мы её записали и разберёмся; обычно помогает обновить страницу." detail={failed.message} />;
  }
}

/** Кусок сборки не загрузился — это выкладка или связь, а не ошибка раздела. */
function isChunkError(error: Error): boolean {
  return /dynamically imported module|Importing a module script failed|Failed to fetch|Loading chunk|ChunkLoadError|preload CSS/i.test(error.message);
}

/** Через сколько пустое ожидание раздела превращается в кнопку. */
const PAGE_WAIT_MS = 6_000;

/**
 * Ожидание раздела.
 *
 * Сначала пустотой: кусок обычно приезжает за десятки миллисекунд, и
 * мигнувшая надпись заметнее самой загрузки. Но если связь подвисла,
 * пустота остаётся навсегда — и человеку нечем её прервать, кроме как
 * догадаться обновить страницу.
 */
function PageWait(): JSX.Element {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), PAGE_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!slow) return <div className="shell-wait" />;
  return <PageTrouble text="Раздел грузится дольше обычного — похоже, подвисла связь." />;
}

function PageTrouble({ text, detail }: { text: string; detail?: string }): JSX.Element {
  return (
    <div className="shell-wait shell-wait--trouble" role="status">
      <p>{text}</p>
      {detail && <code className="shell-wait-detail">{detail.slice(0, 200)}</code>}
      <button
        type="button"
        className="shell-wait-reload"
        onClick={() => globalThis.location.reload()}
      >
        Обновить
      </button>
    </div>
  );
}

/**
 * Полоса о перечитанном профиле.
 *
 * Поверх всех разделов, а не на одном: правка могла прийти в любую
 * минуту и застать человека где угодно. Закрывается им самим —
 * исчезающая сама собой полоса вернула бы ровно ту неизвестность, ради
 * устранения которой её и показывают.
 */
function SyncNotice(): JSX.Element | null {
  const notice = useTerminal((s) => s.syncNotice);
  if (!notice) return null;

  return (
    <div className="shell-notice" role="status">
      <span>{notice}</span>
      <button
        type="button"
        className="shell-notice-close"
        aria-label="Понятно"
        onClick={() => useTerminal.getState().setSyncNotice(null)}
      >
        ✕
      </button>
    </div>
  );
}

/** Сколько событий человек ещё не видел. */
function useUnread(): number {
  const seen = useSeenAt();
  const [times, setTimes] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = (): void => {
      void coreApi
        .signals({ limit: 100 })
        .then(({ signals }: { signals: Signal[] }) => {
          if (!cancelled) setTimes(signals.map((signal) => signal.createdAt));
        })
        .catch(() => {
          // Core не отвечает — значка просто нет. Показать старое число
          // значило бы обещать события, которых, может, уже нет.
          if (!cancelled) setTimes([]);
        });
    };

    load();
    const timer = setInterval(load, UNREAD_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return unreadCount(times, seen);
}
